import React, { useState } from 'react';
import './Sidebar.css';
import { formatVisits, formatVisitors, formatDayVisits, formatDayVisitsPerVisitorRatio, isLowSignalQuality } from '../utils/formatMetrics';
import ProfileFilter from './filters/ProfileFilter';
import SocialGradeFilter from './filters/SocialGradeFilter';
import TenureFilter from './filters/TenureFilter';
import EmploymentFilter from './filters/EmploymentFilter';
import EducationFilter from './filters/EducationFilter';
import IMDFilter from './filters/IMDFilter';
import PopulationFilter from './filters/PopulationFilter';
import VisitorsFilter from './filters/VisitorsFilter';
import SpendPerPersonFilter from './filters/SpendPerPersonFilter';
import CustomDropdown from './CustomDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faStore, faGear, faRightFromBracket, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useAppState, APP_STATES } from '../contexts/AppStateContext';

const Sidebar = ({ user, onLogout, hexData, selectedLocation, loading, colorMetric, onColorMetricChange, selectedHex, analysisName, onDeselectHex, onExploreOrigins, instructionsOpen, onToggleInstructions, onLoadAnalysis, activeFilters, onFiltersChange, originsData, onOpenSettings, onCompareAnalysis, onLocationSummary, onOpenBrandBrowser, destinationHexData, selectedPostcode, destinationActivityFilter, onDestinationActivityFilterChange, onDestinationsSummary, layers, onToggleLayer, brandLayers, brandLayerVisibility, onToggleBrandLayer, onDeleteBrandLayer, minVisitorsThreshold, onMinVisitorsThresholdChange, colorWeighting, onColorWeightingChange, clusterHexResolution, onClusterHexResolutionChange, clusterThreshold, onClusterThresholdChange }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { currentState } = useAppState();

  // Check if filters should be enabled
  const filtersEnabled = currentState === APP_STATES.ORIGINS_DISPLAYED;

  // Filter management
  const addFilter = (filterType) => {
    if (filterType === '') return;

    // Check if filter already exists
    if (activeFilters.some(f => f.type === filterType)) {
      return;
    }

    const newFilter = {
      id: Date.now(),
      type: filterType,
      values: getDefaultFilterValues(filterType)
    };

    onFiltersChange([...activeFilters, newFilter]);
  };

  const getDefaultFilterValues = (filterType) => {
    switch (filterType) {
      case 'profile':
        return { ageProfiles: [], incomeProfiles: [] };
      case 'socialGrade':
        return { grades: [] };
      case 'tenure':
        return { tenureTypes: [] };
      case 'employment':
        return { employmentTypes: [] };
      case 'education':
        return { minLevel: 0, maxLevel: 5 };
      case 'imd':
        return { minDecile: 1, maxDecile: 10 };
      case 'population':
        return { minPopulation: 0, maxPopulation: 100000 };
      case 'visitors':
        return { percentage: 100 };
      case 'spendPerPerson':
        return { selectedCategory: null, minSpend: 0, maxSpend: 1000 };
      default:
        return {};
    }
  };

  const updateFilter = (updatedFilter) => {
    onFiltersChange(
      activeFilters.map(f => f.id === updatedFilter.id ? updatedFilter : f)
    );
  };

  const removeFilter = (filterId) => {
    onFiltersChange(activeFilters.filter(f => f.id !== filterId));
  };

  const renderFilter = (filter) => {
    const commonProps = {
      key: filter.id,
      filter,
      onUpdate: updateFilter,
      onRemove: () => removeFilter(filter.id)
    };

    switch (filter.type) {
      case 'profile':
        return <ProfileFilter {...commonProps} />;
      case 'socialGrade':
        return <SocialGradeFilter {...commonProps} />;
      case 'tenure':
        return <TenureFilter {...commonProps} />;
      case 'employment':
        return <EmploymentFilter {...commonProps} />;
      case 'education':
        return <EducationFilter {...commonProps} />;
      case 'imd':
        return <IMDFilter {...commonProps} />;
      case 'population':
        return <PopulationFilter {...commonProps} originsData={originsData} />;
      case 'visitors':
        return <VisitorsFilter {...commonProps} />;
      case 'spendPerPerson':
        return <SpendPerPersonFilter {...commonProps} originsData={originsData} />;
      default:
        return null;
    }
  };

  // Calculate statistics
  const hexesWithData = hexData ? hexData.hexagons.filter(h => h.devices !== null || h.uniques !== null).length : 0;
  const totalDevices = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.devices || 0), 0) : 0;
  const totalUniques = hexData ? hexData.hexagons.reduce((sum, h) => sum + (h.uniques || 0), 0) : 0;
  const avgDevices = hexesWithData > 0 ? (totalDevices / hexesWithData) : 0;

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img src="/logo-sidebar.svg" alt="Venue IQ" className="sidebar-logo" />
        <div className="user-info">
          <span>{user?.email}</span>
          <div className="header-actions">
            <button onClick={onLoadAnalysis} className="icon-btn" title="Load saved analysis">
              <FontAwesomeIcon icon={faFolderOpen} />
            </button>
            <button onClick={onOpenBrandBrowser} className="icon-btn" title="Brand Browser">
              <FontAwesomeIcon icon={faStore} />
            </button>
            <button onClick={onOpenSettings} className="icon-btn" title="Settings">
              <FontAwesomeIcon icon={faGear} />
            </button>
            <button onClick={onLogout} className="icon-btn logout-btn" title="Logout">
              <FontAwesomeIcon icon={faRightFromBracket} />
            </button>
          </div>
        </div>
      </div>

      <div className="sidebar-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'filters' ? 'active' : ''}`}
          onClick={() => setActiveTab('filters')}
        >
          Filters
        </button>
      </div>

      <div className="sidebar-content">
        {activeTab === 'overview' && (
          <>
            <section className={`sidebar-section instructions-section ${instructionsOpen ? 'open' : 'collapsed'}`}>
          <h2 onClick={onToggleInstructions} className="collapsible-header">
            Instructions
            <span className="toggle-icon">{instructionsOpen ? '−' : '+'}</span>
          </h2>
          {instructionsOpen && (
            <div className="instructions-content">
              <p>Click anywhere on the UK map to analyze that location. The system will show a heatmap of activity around that location. You can toggle between Visits and Visitors in the Controls box below.</p>
            </div>
          )}
        </section>

        {/* Layers Section */}
        {(hexData || originsData || destinationHexData || brandLayers.length > 0) && (
          <section className="sidebar-section">
            <h2>Layers</h2>
            <div className="control-group">
              {hexData && (
                <label className="control-item">
                  <input
                    type="radio"
                    name="activeLayer"
                    value="heatmap"
                    checked={layers.heatmap}
                    onChange={() => onToggleLayer('heatmap')}
                  />
                  <span>Hexmap</span>
                </label>
              )}
              {originsData && (
                <label className="control-item">
                  <input
                    type="radio"
                    name="activeLayer"
                    value="origins"
                    checked={layers.origins}
                    onChange={() => onToggleLayer('origins')}
                  />
                  <span>Origins</span>
                </label>
              )}
              {destinationHexData && selectedPostcode && (
                <label className="control-item">
                  <input
                    type="radio"
                    name="activeLayer"
                    value="destinations"
                    checked={!!layers.destinations}
                    onChange={() => onToggleLayer('destinations')}
                  />
                  <span>{selectedPostcode} Destinations</span>
                </label>
              )}
            </div>

            {/* Brand Layers - Multiple brands with individual toggles and delete buttons */}
            {brandLayers.length > 0 && (
              <div className="control-group" style={{ marginTop: '1rem', borderTop: '1px solid #34495e', paddingTop: '0.5rem' }}>
                {brandLayers.map(brand => (
                  <div key={brand.brand_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label className="control-item" style={{ flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={brandLayerVisibility[brand.brand_id] !== false}
                        onChange={() => onToggleBrandLayer(brand.brand_id)}
                      />
                      <span>{brand.brand_name}</span>
                    </label>
                    <button
                      onClick={() => onDeleteBrandLayer(brand.brand_id)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#fff',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        padding: '0.25rem 0.5rem'
                      }}
                      title="Remove brand layer"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {loading && (
          <section className="sidebar-section">
            <div className="loading-info">
              <div className="loading-spinner-small"></div>
              <p>Analyzing location...</p>
            </div>
          </section>
        )}

        {hexData && !layers.destinations && (
          <section className="sidebar-section">
            <h2>Location Details</h2>

            {!selectedHex && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '4px',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#667eea', fontSize: '1.1rem' }} />
                <span style={{ color: '#667eea', fontSize: '0.9rem' }}>
                  Click on a hex to get the option to explore it in more detail
                </span>
              </div>
            )}

            {selectedHex && (
              <>
                <div className="hex-info">
                  {analysisName ? (
                    <p><strong>Location:</strong> {analysisName}</p>
                  ) : (
                    <p><strong>Hex ID:</strong> {selectedHex.hex_id}</p>
                  )}
                  <p><strong>Visits:</strong> {formatVisits(selectedHex.devices)}</p>
                  <p><strong>Day Visits:</strong> {formatDayVisits(selectedHex.devices)}</p>
                  <p><strong>Visitors:</strong> {formatVisitors(selectedHex.uniques)}</p>
                  {isLowSignalQuality(selectedHex.devices, selectedHex.uniques) && (
                    <p style={{ color: '#e67e22', fontSize: '0.85rem', fontStyle: 'italic' }}>
                      <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.4rem' }} />
                      Reduced confidence due to signal noise
                    </p>
                  )}
                </div>
                <div className="explore-actions">
                  {currentState === APP_STATES.HEX_SELECTED && (
                    <button
                      className="explore-btn"
                      onClick={onExploreOrigins}
                      disabled={loading}
                    >
                      {loading ? 'Loading Origins...' : 'Explore Origins'}
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Show Clear Selection button whenever there's an active analysis */}
            {(selectedHex || analysisName) && (
              <div className="explore-actions">
                <button className="deselect-btn" onClick={onDeselectHex}>
                  Clear Selection
                </button>
              </div>
            )}

            <div className="hex-stats">
              <h3>Activity Statistics</h3>
              <p>
                <strong>Avg Visits/Hex:</strong> {formatVisits(avgDevices)}
              </p>
              {hexesWithData > 0 && totalUniques > 0 && (
                <p>
                  <strong>Day Visits/Visitor Ratio:</strong>{' '}
                  {formatDayVisitsPerVisitorRatio(totalDevices, totalUniques)}
                </p>
              )}
            </div>

            <button
              className="compare-analysis-btn"
              onClick={onCompareAnalysis}
              disabled={loading || !originsData}
            >
              Compare Analysis
            </button>

            <button
              className="location-summary-btn"
              onClick={onLocationSummary}
              disabled={loading || !originsData}
            >
              Location Summary Report
            </button>
          </section>
        )}

        {destinationHexData && selectedPostcode && layers.destinations && (
          <section className="sidebar-section">
            <h2>Postcode Destinations</h2>
            <div className="hex-info">
              <p><strong>Source Postcode:</strong> {selectedPostcode}</p>
              <p><strong>Total Destination Hexes:</strong> {destinationHexData.destinations?.length || 0}</p>

              <div className="control-group" style={{ marginTop: '1rem' }}>
                <h3>Activity Filter:</h3>
                <label className="control-item">
                  <input
                    type="radio"
                    name="destinationActivity"
                    value="high"
                    checked={destinationActivityFilter === 'high'}
                    onChange={(e) => onDestinationActivityFilterChange(e.target.value)}
                  />
                  <span>High Activity</span>
                </label>
                <label className="control-item">
                  <input
                    type="radio"
                    name="destinationActivity"
                    value="all"
                    checked={destinationActivityFilter === 'all'}
                    onChange={(e) => onDestinationActivityFilterChange(e.target.value)}
                  />
                  <span>All Activity</span>
                </label>
                <label className="control-item">
                  <input
                    type="radio"
                    name="destinationActivity"
                    value="clustered"
                    checked={destinationActivityFilter === 'clustered'}
                    onChange={(e) => onDestinationActivityFilterChange(e.target.value)}
                  />
                  <span>Clustered Activity (Regional Level)</span>
                </label>
              </div>

              <p style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.75rem', fontStyle: 'italic' }}>
                {destinationActivityFilter === 'high'
                  ? `Showing ${destinationHexData.destinations?.filter(d => d.total_occurences >= minVisitorsThreshold / 19.2).length || 0} high-activity destinations (≥${minVisitorsThreshold.toLocaleString()} visitors)`
                  : destinationActivityFilter === 'clustered'
                  ? `Showing destinations clustered at ${clusterHexResolution === 5 ? 'regional' : 'sub-regional'} level (~${clusterHexResolution === 5 ? '150' : '60'}km hexes) with ≥${clusterThreshold.toLocaleString()} visitors`
                  : 'Showing all destinations visited by people from this postcode'}
              </p>

              {/* Button to view destinations summary when clustered */}
              {destinationActivityFilter === 'clustered' && (
                <button
                  className="btn btn-primary"
                  onClick={onDestinationsSummary}
                  style={{ marginTop: '0.75rem', width: '100%' }}
                >
                  View Destinations Table
                </button>
              )}
            </div>
          </section>
        )}

        {/* Destination Map Controls - only shown when destinations layer is active */}
        {destinationHexData && selectedPostcode && layers.destinations && (
          <section className="sidebar-section">
            <h2>Destination Map Controls</h2>

            {/* High Activity Controls */}
            {destinationActivityFilter === 'high' && (
              <div className="control-group">
                <h3>Min Number of Visitors: {minVisitorsThreshold.toLocaleString()}</h3>
                <input
                  type="range"
                  min="10000"
                  max="400000"
                  step="10000"
                  value={minVisitorsThreshold}
                  onChange={(e) => onMinVisitorsThresholdChange(parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.25rem' }}>
                  <span>10k</span>
                  <span>400k</span>
                </div>
              </div>
            )}

            {/* All Activity Controls */}
            {destinationActivityFilter === 'all' && (
              <div className="control-group">
                <h3>Color Weighting:</h3>
                <label className="control-item">
                  <input
                    type="radio"
                    name="colorWeighting"
                    value="even"
                    checked={colorWeighting === 'even'}
                    onChange={(e) => onColorWeightingChange(e.target.value)}
                  />
                  <span>Even Colour Weighting</span>
                </label>
                <label className="control-item">
                  <input
                    type="radio"
                    name="colorWeighting"
                    value="lowEnd"
                    checked={colorWeighting === 'lowEnd'}
                    onChange={(e) => onColorWeightingChange(e.target.value)}
                  />
                  <span>Low-End Colour Weighting</span>
                </label>
                <p style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  {colorWeighting === 'lowEnd'
                    ? 'More color steps at lower visitor counts for better differentiation'
                    : 'Equal color distribution across visitor range'}
                </p>
              </div>
            )}

            {/* Clustered Activity Controls */}
            {destinationActivityFilter === 'clustered' && (
              <>
                <div className="control-group">
                  <h3>Cluster Size:</h3>
                  <label className="control-item">
                    <input
                      type="radio"
                      name="clusterResolution"
                      value="5"
                      checked={clusterHexResolution === 5}
                      onChange={(e) => onClusterHexResolutionChange(parseInt(e.target.value))}
                    />
                    <span>Larger (~150km hexes)</span>
                  </label>
                  <label className="control-item">
                    <input
                      type="radio"
                      name="clusterResolution"
                      value="6"
                      checked={clusterHexResolution === 6}
                      onChange={(e) => onClusterHexResolutionChange(parseInt(e.target.value))}
                    />
                    <span>Smaller (~60km hexes)</span>
                  </label>
                </div>

                <div className="control-group">
                  <h3>Min Visitors Threshold: {clusterThreshold.toLocaleString()}</h3>
                  <input
                    type="range"
                    min="100000"
                    max="2000000"
                    step="100000"
                    value={clusterThreshold}
                    onChange={(e) => onClusterThresholdChange(parseInt(e.target.value))}
                    style={{ width: '100%', marginTop: '0.5rem' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.25rem' }}>
                    <span>100k</span>
                    <span>2M</span>
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {(currentState === APP_STATES.HEATMAP_LOCATION_SELECTED || currentState === APP_STATES.HEX_SELECTED) && (
          <section className="sidebar-section">
            <h2>Visualisation Controls</h2>
            <div className="controls">
              <div className="control-group">
                <h3>Color Hexagons By:</h3>
                <label className="control-item">
                  <input
                    type="radio"
                    name="colorMetric"
                    value="devices"
                    checked={colorMetric === 'devices'}
                    onChange={(e) => onColorMetricChange(e.target.value)}
                  />
                  <span>Visits</span>
                </label>
                <label className="control-item">
                  <input
                    type="radio"
                    name="colorMetric"
                    value="uniques"
                    checked={colorMetric === 'uniques'}
                    onChange={(e) => onColorMetricChange(e.target.value)}
                  />
                  <span>Visitors</span>
                </label>
              </div>
            </div>
          </section>
        )}
          </>
        )}

        {activeTab === 'filters' && (
          <>
            {!filtersEnabled ? (
              <section className="sidebar-section">
                <div style={{
                  padding: '1rem',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  border: '1px solid rgba(102, 126, 234, 0.3)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ color: '#667eea', fontSize: '1.2rem', marginTop: '0.2rem', flexShrink: 0 }}
                    />
                    <div style={{ color: '#2c3e50', lineHeight: '1.6' }}>
                      <p style={{ margin: 0 }}>
                        Filters are only available when viewing the Postcode layer. Switch back to the{' '}
                        <button
                          onClick={() => setActiveTab('overview')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#667eea',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit',
                            fontWeight: '600'
                          }}
                        >
                          Overview tab
                        </button>
                        , select a location/hex and select Explore Origins to load a postcode layer and enable the filters.
                      </p>
                      <p style={{ margin: '0.75rem 0 0 0' }}>
                        Alternatively, load a previously saved analysis from the{' '}
                        <button
                          onClick={onLoadAnalysis}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#667eea',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit',
                            fontWeight: '600'
                          }}
                        >
                          load button
                        </button>
                        {' '}at the top-left of the screen.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <>
                <section className="sidebar-section add-filter-section">
                  <h3>Add Filter</h3>
                  <CustomDropdown
                    value=""
                    onChange={(value) => addFilter(value)}
                    disabled={!originsData}
                    placeholder="Select a filter type..."
                    options={[
                      { value: '', label: 'Select a filter type...' },
                      { value: 'profile', label: 'Profile (Age & Income)' },
                      { value: 'socialGrade', label: 'Social Grade' },
                      { value: 'tenure', label: 'Tenure' },
                      { value: 'employment', label: 'Employment' },
                      { value: 'education', label: 'Education' },
                      { value: 'imd', label: 'IMD Decile' },
                      { value: 'population', label: 'Population' },
                      { value: 'visitors', label: 'Visitors (Cumulative %)' },
                      { value: 'spendPerPerson', label: 'Spend Per Person' }
                    ]}
                  />
                  {!originsData && (
                    <p style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      Select a hex and explore origins to enable filters
                    </p>
                  )}
                </section>

                {activeFilters.length === 0 ? (
                  <div className="no-filters-message">
                    No filters active. Add a filter above to start filtering postcodes.
                  </div>
                ) : (
                  activeFilters.map(filter => renderFilter(filter))
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
